import { Select, ActionIcon, Group, Modal, Tooltip, Input, Stack, TextInput, PasswordInput, Button, SegmentedControl, Switch } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { TEACHER_PARAMS } from '../lib/roles';
import { PhoneInput } from './PhoneInput';
import { MultiPhoneEditor, MultiAddressEditor, PhoneItem, AddressItem } from './MultiContact';
import { ServerSearchSelect } from './ServerSearchSelect';

interface Opt { value: string; label: string }

/**
 * Select + "+" — variant ro'yxatdan tanlaydi yoki o'sha yerda (sahifadan
 * chiqmasdan) yangi yaratadi. `renderCreate` yaratilgach ID ni qaytaradi.
 */
export function CreatableSelect({
  label, placeholder, required, data, value, onChange, createTitle, renderCreate, error, searchable = true,
}: {
  label?: string;
  placeholder?: string;
  required?: boolean;
  data: Opt[];
  value: string | null;
  onChange: (v: string | null) => void;
  createTitle: string;
  renderCreate: (onCreated: (id: string) => void) => React.ReactNode;
  error?: React.ReactNode;
  searchable?: boolean;
}) {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  return (
    <>
      <Input.Wrapper label={label} required={required} error={error}>
        <Group gap="xs" wrap="nowrap" mt={4}>
          <Select
            style={{ flex: 1 }}
            placeholder={placeholder || t('qc.selectPlaceholder')}
            data={data}
            value={value}
            onChange={onChange}
            searchable={searchable}
            clearable={!required}
            nothingFoundMessage={t('qc.nothingFound')}
            comboboxProps={{ withinPortal: true }}
          />
          <Tooltip label={t('qc.addNew')}>
            <ActionIcon variant="light" size={36} onClick={open} aria-label="add">
              <IconPlus size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Input.Wrapper>

      <Modal opened={opened} onClose={close} title={createTitle} zIndex={500}>
        {renderCreate((id) => { onChange(id); close(); })}
      </Modal>
    </>
  );
}

/**
 * Serverdan qidiruvli CreatableSelect — katta ro'yxatlar (ota-ona, o'quvchi...) uchun.
 * Ma'lumot sahifalangan bo'lgani uchun qidiruv har doim serverda bajariladi.
 */
export function ServerCreatableSelect({
  label, placeholder, required, endpoint, mapOption, params, value, onChange, createTitle, renderCreate, error, currentOption,
}: {
  label?: string;
  placeholder?: string;
  required?: boolean;
  endpoint: string;
  mapOption: (item: never) => Opt;
  params?: Record<string, string | undefined>;
  value: string | null;
  onChange: (v: string | null) => void;
  createTitle: string;
  renderCreate: (onCreated: (id: string) => void) => React.ReactNode;
  error?: React.ReactNode;
  currentOption?: Opt | null;
}) {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  return (
    <>
      <Input.Wrapper label={label} required={required} error={error}>
        <Group gap="xs" wrap="nowrap" mt={4}>
          <ServerSearchSelect
            style={{ flex: 1 }}
            placeholder={placeholder || t('qc.searchPlaceholder')}
            endpoint={endpoint}
            mapOption={mapOption}
            params={params}
            value={value}
            onChange={onChange}
            currentOption={currentOption}
            comboboxProps={{ withinPortal: true }}
          />
          <Tooltip label={t('qc.addNew')}>
            <ActionIcon variant="light" size={36} onClick={open} aria-label="add">
              <IconPlus size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Input.Wrapper>

      <Modal opened={opened} onClose={close} title={createTitle} zIndex={500}>
        {renderCreate((id) => { onChange(id); close(); })}
      </Modal>
    </>
  );
}

/** Jinsni tanlash (majburiy) — Erkak / Ayol. */
export function GenderInput({ value, onChange, error }: { value?: string; onChange: (v: string) => void; error?: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Input.Wrapper label={t('qc.gender')} required error={error}>
      <SegmentedControl
        fullWidth
        mt={4}
        data={[{ value: 'MALE', label: t('qc.male') }, { value: 'FEMALE', label: t('qc.female') }]}
        value={value || ''}
        onChange={onChange}
      />
    </Input.Wrapper>
  );
}

// ─── Tez yaratish formalari ───────────────────────────────────

/**
 * O'quv yili.
 *
 * `isActive` ENDI KO'RINADIGAN TANLOV. Ilgari u qattiq `true` edi: sinf yaratish
 * oynasidan "yo'l-yo'lakay" qo'shilgan yil joriy faol yilni JIMGINA almashtirib
 * yuborardi (backend bitta faol yil qoldiradi) — jurnal, choraklar va hisobotlar
 * esa faol yilga bog'langan. Standart qiymat `true` bo'lib qoladi (bo'sh tizimda
 * birinchi yil darhol ishlashi kerak), lekin foydalanuvchi buni ko'radi va o'chira oladi.
 */
export function QuickYearForm({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm({
    initialValues: { name: '', isActive: true },
    validate: { name: (v) => (!v ? t('qc.yearNameRequired') : null) },
  });
  const mut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/classes/academic-years', v),
    onSuccess: (res) => {
      notifications.show({ color: 'green', message: t('qc.yearAdded') });
      qc.invalidateQueries({ queryKey: ['academic-years'] });
      qc.invalidateQueries({ queryKey: ['academic-terms'] });
      qc.invalidateQueries({ queryKey: ['academic-term-current'] });
      onCreated(res.data.id);
    },
  });
  return (
    <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
      <Stack>
        <TextInput label={t('qc.academicYear')} placeholder="2025-2026" required {...form.getInputProps('name')} />
        <Switch
          label={t('terms.makeActive')}
          description={t('terms.activeHint')}
          checked={form.values.isActive}
          // Qiymat updater ICHIDA emas, TASHQARIDA o'qiladi (oq ekran tuzog'i).
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            form.setFieldValue('isActive', checked);
          }}
        />
        <Button type="submit" loading={mut.isPending}>{t('qc.save')}</Button>
      </Stack>
    </form>
  );
}

/** O'qituvchi (login/parol/jins bilan). */
export function QuickTeacherForm({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm({
    initialValues: { fullName: '', login: '', password: '', gender: '', phone: '' },
    validate: {
      fullName: (v) => (!v ? t('qc.fullNameRequired') : null),
      login: (v) => (v.length < 3 ? t('qc.loginMinLength') : null),
      password: (v) => (v.length < 5 ? t('qc.passwordMinLength') : null),
      gender: (v) => (!v ? t('qc.genderRequired') : null),
    },
  });
  const mut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/staff', { ...v, role: 'TEACHER' }),
    onSuccess: (res) => { notifications.show({ color: 'green', message: t('qc.teacherAdded') }); qc.invalidateQueries({ queryKey: ['teachers'] }); qc.invalidateQueries({ queryKey: ['staff-all'] }); qc.invalidateQueries({ queryKey: ['teachers-page'] }); onCreated(res.data.id); },
  });
  return (
    <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
      <Stack>
        <TextInput label={t('qc.fullName')} required {...form.getInputProps('fullName')} />
        <GenderInput {...form.getInputProps('gender')} />
        <TextInput label={t('qc.login')} required {...form.getInputProps('login')} />
        <PasswordInput label={t('qc.password')} required {...form.getInputProps('password')} />
        <PhoneInput label={t('qc.phone')} {...form.getInputProps('phone')} />
        <Button type="submit" loading={mut.isPending}>{t('qc.save')}</Button>
      </Stack>
    </form>
  );
}

/** Ota-ona akkaunti (o'quvchi yaratishda). Keyin o'quvchiga biriktiriladi. */
export function QuickParentForm({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm({
    initialValues: { fullName: '', login: '', password: '', gender: '', phones: [] as PhoneItem[], addresses: [] as AddressItem[] },
    validate: {
      fullName: (v) => (!v ? t('qc.fullNameRequired') : null),
      login: (v) => (v.length < 3 ? t('qc.loginMinLength') : null),
      password: (v) => (v.length < 5 ? t('qc.passwordMinLength') : null),
      gender: (v) => (!v ? t('qc.genderRequired') : null),
    },
  });
  const mut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/students/parents', v),
    onSuccess: (res) => { notifications.show({ color: 'green', message: t('qc.parentCreated') }); qc.invalidateQueries({ queryKey: ['parents-list'] }); onCreated(res.data.id); },
  });
  return (
    <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
      <Stack>
        <TextInput label={t('qc.parentFullName')} required {...form.getInputProps('fullName')} />
        <GenderInput {...form.getInputProps('gender')} />
        <TextInput label={t('qc.login')} required {...form.getInputProps('login')} />
        <PasswordInput label={t('qc.password')} required {...form.getInputProps('password')} />
        <MultiPhoneEditor value={form.values.phones} onChange={(v) => form.setFieldValue('phones', v)} />
        <MultiAddressEditor value={form.values.addresses} onChange={(v) => form.setFieldValue('addresses', v)} />
        <Button type="submit" loading={mut.isPending}>{t('qc.save')}</Button>
      </Stack>
    </form>
  );
}

/** Fan. */
export function QuickSubjectForm({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm({ initialValues: { name: '', code: '' }, validate: { name: (v) => (!v ? t('qc.subjectNameRequired') : null) } });
  const mut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/subjects', v),
    onSuccess: (res) => { notifications.show({ color: 'green', message: t('qc.subjectAdded') }); qc.invalidateQueries({ queryKey: ['subjects'] }); onCreated(res.data.id); },
  });
  return (
    <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
      <Stack>
        <TextInput label={t('qc.subjectName')} placeholder={t('qc.subjectNamePlaceholder')} required {...form.getInputProps('name')} />
        <TextInput label={t('qc.code')} placeholder="MATH" {...form.getInputProps('code')} />
        <Button type="submit" loading={mut.isPending}>{t('qc.save')}</Button>
      </Stack>
    </form>
  );
}

/** Sinf (o'quv yili + sinf rahbari inline yaratish bilan). */
export function QuickClassForm({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: years } = useQuery({ queryKey: ['academic-years'], queryFn: async () => (await api.get<Opt[] | { id: string; name: string }[]>('/classes/academic-years')).data as { id: string; name: string }[] });
  const { data: teachers } = useQuery({ queryKey: ['teachers'], queryFn: async () => (await api.get<{ id: string; fullName: string }[]>('/staff', { params: TEACHER_PARAMS })).data });

  const form = useForm({
    initialValues: { name: '', academicYearId: '', homeroomTeacherId: '' },
    validate: { name: (v) => (!v ? t('qc.classNameRequired') : null), academicYearId: (v) => (!v ? t('qc.academicYearRequired') : null) },
  });
  const mut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/classes', { ...v, homeroomTeacherId: v.homeroomTeacherId || undefined }),
    onSuccess: (res) => { notifications.show({ color: 'green', message: t('qc.classAdded') }); qc.invalidateQueries({ queryKey: ['classes'] }); onCreated(res.data.id); },
  });

  return (
    <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
      <Stack>
        <TextInput label={t('qc.className')} placeholder="5-A" required {...form.getInputProps('name')} />
        <CreatableSelect
          label={t('qc.academicYear')}
          required
          data={(years ?? []).map((y) => ({ value: y.id, label: y.name }))}
          value={form.values.academicYearId}
          onChange={(v) => form.setFieldValue('academicYearId', v || '')}
          error={form.errors.academicYearId}
          createTitle={t('qc.newAcademicYear')}
          renderCreate={(onCreated2) => <QuickYearForm onCreated={onCreated2} />}
        />
        <CreatableSelect
          label={t('qc.homeroomTeacher')}
          data={(teachers ?? []).map((tt) => ({ value: tt.id, label: tt.fullName }))}
          value={form.values.homeroomTeacherId}
          onChange={(v) => form.setFieldValue('homeroomTeacherId', v || '')}
          createTitle={t('qc.newTeacher')}
          renderCreate={(onCreated2) => <QuickTeacherForm onCreated={onCreated2} />}
        />
        <Button type="submit" loading={mut.isPending}>{t('qc.save')}</Button>
      </Stack>
    </form>
  );
}
